import { Flex, Stack } from "../../mantine-passthroughs/index.js";
import { Body1 } from "../../typography/Body1.js";
import { FormFieldError } from "../form-field-error/FormFieldError.js";
import { TextInput } from "../text-input/TextInput.js";
import { useRef } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { DateAdapter } from "@myphx-engineering/date-utils/client";

//#region src/components/inputs/date-of-birth/DateOfBirthField.tsx
const DateOfBirthField = ({ labels, monthProps, dayProps, yearProps, groupError, onGroupValidation, "data-testid": testId }) => {
	const { t } = useTranslation("pds");
	const monthRef = useRef(null);
	const dayRef = useRef(null);
	const yearRef = useRef(null);
	const { error: monthError, onChange: onMonthChange, onBlur: onMonthBlur, ...restMonthProps } = monthProps;
	const { error: dayError, onChange: onDayChange, onBlur: onDayBlur, ...restDayProps } = dayProps;
	const { error: yearError, onBlur: onYearBlur, ...restYearProps } = yearProps;
	const runCrossFieldValidation = () => {
		if (!onGroupValidation) return;
		const month = monthRef.current?.value ?? "";
		const day = dayRef.current?.value ?? "";
		const year = yearRef.current?.value ?? "";
		const monthNum = Number.parseInt(month, 10);
		const dayNum = Number.parseInt(day, 10);
		const yearNum = Number.parseInt(year, 10);
		if (Number.isNaN(monthNum) || monthNum < 1 || monthNum > 12) return;
		if (Number.isNaN(dayNum) || dayNum < 1 || dayNum > 31) return;
		if (Number.isNaN(yearNum) || year.length !== 4 || yearNum < 1900 || yearNum > (/* @__PURE__ */ new Date()).getFullYear()) return;
		if (!DateAdapter.isValidDate(yearNum, monthNum, dayNum)) {
			onGroupValidation(t("common.formErrors.dob.invalid"));
			return;
		}
		if (DateAdapter.isFutureDate(yearNum, monthNum, dayNum)) {
			onGroupValidation(t("common.formErrors.dob.future"));
			return;
		}
		onGroupValidation(null);
	};
	const handleMonthChange = (e) => {
		const value = e.target.value.replace(/\D/g, "");
		onMonthChange?.(e);
		if (value.length >= 2) dayRef.current?.focus();
	};
	const handleDayChange = (e) => {
		const value = e.target.value.replace(/\D/g, "");
		onDayChange?.(e);
		if (value.length >= 2) yearRef.current?.focus();
	};
	const handleMonthBlur = (e) => {
		onMonthBlur?.(e);
		setTimeout(runCrossFieldValidation, 0);
	};
	const handleDayBlur = (e) => {
		onDayBlur?.(e);
		setTimeout(runCrossFieldValidation, 0);
	};
	const handleYearBlur = (e) => {
		onYearBlur?.(e);
		setTimeout(runCrossFieldValidation, 0);
	};
	return /* @__PURE__ */ jsxs(Stack, {
		gap: "xxs",
		"data-testid": testId,
		children: [
			/* @__PURE__ */ jsx(Body1, {
				bold: true,
				color: "gray",
				highContrast: true,
				children: labels.heading
			}),
			/* @__PURE__ */ jsxs(Flex, {
				gap: "sm",
				children: [
					/* @__PURE__ */ jsx(TextInput, {
						flex: 1,
						name: "dobMonth",
						placeholder: labels.month,
						inputMode: "numeric",
						required: true,
						...restMonthProps,
						error: groupError ? true : monthError,
						ref: monthRef,
						onChange: handleMonthChange,
						onBlur: handleMonthBlur
					}),
					/* @__PURE__ */ jsx(TextInput, {
						flex: 1,
						name: "dobDay",
						placeholder: labels.day,
						inputMode: "numeric",
						required: true,
						...restDayProps,
						error: groupError ? true : dayError,
						ref: dayRef,
						onChange: handleDayChange,
						onBlur: handleDayBlur
					}),
					/* @__PURE__ */ jsx(TextInput, {
						flex: 1,
						name: "dobYear",
						placeholder: labels.year,
						inputMode: "numeric",
						required: true,
						...restYearProps,
						error: groupError ? true : yearError,
						ref: yearRef,
						onBlur: handleYearBlur
					})
				]
			}),
			groupError && typeof groupError === "string" && /* @__PURE__ */ jsx(FormFieldError, {
				centered: false,
				errText: groupError
			})
		]
	});
};

//#endregion
export { DateOfBirthField };